import { useEffect, useId, useState } from 'react';

import { ApiError } from '@shared/api/client';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { Dialog } from '@shared/ui/dialog';
import { useToast } from '@shared/ui/toast/ToastProvider';

import { Panel } from '@shared/ui/Panel';

import { CaseSection, ColumnButton } from '../_shared/components';
import { sharedCopy } from '../_shared/copy';
import type { CaseDetail, CaseId, ResolutionAction } from '../_shared/types';
import { copy } from './copy';
import { errors } from './errors';
import { useResolveCase } from './mutations';

/**
 * The decision — the three actions the catalog names, and no fourth.
 *
 * **It sits on the case detail, and it is drawn only on a content report.** It
 * used to live on `/reports/:caseId`, a second surface over the same record: the
 * report queue was `GET /admin/cases?caseType=CONTENT_REPORT` and the report
 * detail was `GET /admin/cases/{id}` — the same reads, under a second set of
 * URLs, with a second wording of the same four states. The endpoint behind these
 * three buttons was always `POST /admin/cases/{id}:resolve`, a **case** route, so
 * the decision arrives where the record it decides already was.
 *
 * **The card is the boundary, not decoration.** Left loose under the reply form
 * the three buttons read as a third action row belonging to the composer: an
 * operator scanning down would meet «أرسل الردّ · امسح» and then «اصرف النظر ·
 * ألغِ النشر · احذف» as one continuous strip. The reply addresses the reporter;
 * these buttons touch the title and its owner.
 *
 * **Dismiss is confirmed too, and that is deliberate.** It is not destructive in
 * the same sense — but it closes the report against the person who raised it,
 * from a surface that cannot reopen it. Three actions behind one gate also reads
 * better than two behind a gate and one loose beside them.
 *
 * **After a resolution the buttons disable rather than disappear**, and the line
 * beneath them says what was done and where to change the case's state. A
 * disabled control with no written reason is a defect; a control that vanishes
 * cannot be distinguished from one that was never there.
 */
export function CaseDecision({
  caseId,
  caseDetail,
  canBlockOwner,
  onResolved,
}: {
  caseId: CaseId;
  caseDetail: CaseDetail;
  /**
   * Whether the caller's `backend-support` role admits the block rider. It gates
   * a **control**, never an outcome — the owning service refuses an operator
   * with `403 SUPPORT_BLOCK_REQUIRES_ADMIN` regardless of what was drawn.
   */
  canBlockOwner: boolean;
  onResolved: (message: string) => void;
}) {
  const toast = useToast();
  const resolve = useResolveCase(caseId);
  const [mode, setMode] = useState<ResolutionAction | null>(null);

  const resolved = caseDetail.resolution ?? null;
  const subject = caseDetail.subjectLabel ?? copy.facts.unknown;

  function commit(input: { blockOwner: boolean; body?: string }) {
    if (!mode) return;

    resolve.mutate(
      { resolutionAction: mode, blockOwner: input.blockOwner, ...(input.body ? { body: input.body } : {}) },
      {
        onSuccess: () => {
          setMode(null);
          onResolved(copy.decision.resolved);
        },
        onError: (error) => {
          setMode(null);
          const status = error instanceof ApiError ? error.status : 0;
          const code = error instanceof ApiError ? error.code : undefined;
          toast.show(resolveErrorCopy(code, status, errors).message, 'danger');
        },
      },
    );
  }

  return (
    <>
      <CaseSection id="case-decision" heading={copy.decision.heading}>
        <Panel>
          <div className="p-4 md:p-5">
            <p className="mb-4 max-w-measure text-xs text-ink-muted">{copy.decision.note}</p>

            {/*
              **A grid, not a wrapping flex row.** Three labelled decisions left
              to intrinsic width wrapped two-and-one on a 375 px window: the
              third button landed alone on a second line at a width neither of
              the other two shared, which reads as a control of a different kind
              rather than the third member of one set. At the base tier they
              stack full width — one decision per line, each label whole — and
              from `--bp-sm` they are one row of content-sized columns against
              the reading edge, so the tablet and desktop tiers cannot ragged-
              wrap either.
            */}
            <div className="grid gap-2 sm:auto-cols-max sm:grid-flow-col sm:justify-start">
              <ColumnButton onClick={() => setMode('DISMISS')} disabled={resolved !== null}>
                {copy.decision.dismiss}
              </ColumnButton>

              <ColumnButton onClick={() => setMode('UNPUBLISH_SUBJECT')} disabled={resolved !== null}>
                {copy.decision.unpublish}
              </ColumnButton>

              {/*
                **Set apart from the two beside it, on both axes.** Deleting a
                title takes its files with it, and the gap that separates it from
                «ألغِ النشر» is the one that a slipped press falls into. It is
                also outline-only, never filled: `--danger` sits six degrees from
                `--accent-text`, so a filled destructive button is
                indistinguishable from «play» at the same lightness — the
                distinction is form, not hue.
              */}
              <div className="mt-1 sm:ms-2 sm:mt-0">
                <ColumnButton
                  tone="danger"
                  onClick={() => setMode('DELETE_SUBJECT')}
                  disabled={resolved !== null}
                >
                  {copy.decision.remove}
                </ColumnButton>
              </div>
            </div>

            {resolved ? (
              <p className="mb-0 mt-4 max-w-measure text-xs text-ink-muted">
                {resolutionNote(resolved.resolutionAction)}
              </p>
            ) : null}
          </div>
        </Panel>
      </CaseSection>

      <ResolveDialog
        mode={mode}
        subject={subject}
        owner={caseDetail.subjectOwnerDisplayName ?? null}
        canBlockOwner={canBlockOwner}
        busy={resolve.isPending}
        onCommit={commit}
        onCancel={() => setMode(null)}
      />
    </>
  );
}

function resolutionNote(action: string): string {
  if (action === 'DISMISS') return copy.decision.doneDismiss;
  if (action === 'UNPUBLISH_SUBJECT') return copy.decision.doneUnpublish;
  if (action === 'DELETE_SUBJECT') return copy.decision.doneRemove;
  return copy.decision.doneDismiss;
}

/**
 * One dialog, three modes.
 *
 * The shape is identical across the three — a question naming the title, a
 * paragraph stating the effect, a commit button — so three dialogs would be
 * three copies drifting apart at the first edit. The heading, the body, and the
 * commit label are written from one map, and the commit button's own classes are
 * **rebuilt** per mode rather than accumulated: a button that carried both the
 * commit and the danger treatment across two openings would render whichever
 * rule came last in the sheet instead of what the current opening meant.
 *
 * **The block rider appears in two of the three modes.** The catalog offers it
 * «when a report is resolved *against them*» — so it rides with unpublish and
 * delete and is absent from dismiss, which resolves against nobody. It is a
 * checkbox rather than a fourth button because the row calls it an additional
 * action offered *with* a resolution, not a resolution of its own.
 *
 * **Blocking the owner of a title blocks an operator, and that is stated rather
 * than engineered around.** Every title here is operator-authored, so the owner
 * of a reported title is a colleague. The catalog offers the action without
 * qualification and the control ships as written; hiding it when the owner holds
 * a role would be a role-scoped visibility rule the catalog does not grant.
 */
function ResolveDialog({
  mode,
  subject,
  owner,
  canBlockOwner,
  busy,
  onCommit,
  onCancel,
}: {
  mode: ResolutionAction | null;
  subject: string;
  owner: string | null;
  canBlockOwner: boolean;
  busy: boolean;
  onCommit: (input: { blockOwner: boolean; body?: string }) => void;
  onCancel: () => void;
}) {
  const headingId = useId();
  const blockId = useId();
  const blockHintId = useId();
  const replyId = useId();
  const replyHintId = useId();

  const [blockOwner, setBlockOwner] = useState(false);
  const [body, setBody] = useState('');

  // The dialog is one element serving three decisions, so its controls are
  // reset every time it opens — never left holding the previous mode's rider.
  useEffect(() => {
    if (mode === null) return;
    setBlockOwner(false);
    setBody('');
  }, [mode]);

  if (mode === null) return null;

  const spec =
    mode === 'DISMISS'
      ? copy.dialog.dismiss
      : mode === 'UNPUBLISH_SUBJECT'
        ? copy.dialog.unpublish
        : copy.dialog.remove;

  /** Dismiss resolves against nobody, so the rider has no subject there. */
  const ridesWithThisMode = mode !== 'DISMISS';
  const showBlockOwner = ridesWithThisMode && canBlockOwner && owner !== null;

  return (
    <Dialog
      open
      labelledBy={headingId}
      onClose={(returnValue) => {
        if (returnValue !== 'commit') onCancel();
      }}
    >
      <form
        method="dialog"
        onSubmit={(event) => {
          const submitter = (event.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null;
          if (submitter?.value !== 'commit') return;
          onCommit({
            blockOwner: showBlockOwner && blockOwner,
            ...(body.trim() ? { body: body.trim() } : {}),
          });
        }}
        className="flex flex-col gap-4"
      >
        <h2 id={headingId} className="text-h3">
          {spec.heading(subject)}
        </h2>

        <p className="mb-0 max-w-none text-sm text-ink-muted">{spec.body}</p>

        {showBlockOwner ? (
          <div className="grid gap-2">
            <label htmlFor={blockId} className="hit-target flex items-center gap-3 text-sm text-ink">
              <input
                id={blockId}
                type="checkbox"
                checked={blockOwner}
                onChange={(event) => setBlockOwner(event.currentTarget.checked)}
                aria-describedby={blockHintId}
                className="size-4 accent-accent"
              />
              {copy.dialog.blockOwnerLabel(owner)}
            </label>
            <p id={blockHintId} className="mb-0 max-w-none text-xs text-ink-muted">
              {copy.dialog.blockOwnerHint}
            </p>
          </div>
        ) : null}

        <div className="grid gap-2">
          <label htmlFor={replyId} className="text-sm font-medium text-ink">
            {copy.dialog.replyLabel}
          </label>
          <textarea
            id={replyId}
            rows={3}
            value={body}
            onChange={(event) => setBody(event.currentTarget.value)}
            aria-describedby={replyHintId}
            className="w-full rounded-sm border border-border-strong bg-surface p-3 text-sm leading-normal text-ink [resize:vertical]"
          />
          <p id={replyHintId} className="mb-0 max-w-none text-xs text-ink-muted">
            {copy.dialog.replyHint}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {/*
            Both buttons in a `method="dialog"` form are submits, and a submit
            runs constraint validation — `formNoValidate` keeps the exit open.
          */}
          <button
            type="submit"
            value="cancel"
            formNoValidate
            onClick={onCancel}
            className="rounded-sm border border-border-strong px-5 py-3 text-sm text-ink hover:bg-hover-bg"
          >
            {sharedCopy.action.cancel}
          </button>

          <button
            type="submit"
            value="commit"
            disabled={busy}
            className={
              mode === 'DELETE_SUBJECT'
                ? 'rounded-sm border border-danger px-5 py-3 text-sm text-danger hover:bg-danger-soft disabled:cursor-not-allowed disabled:opacity-45'
                : 'rounded-sm bg-accent px-5 py-3 text-sm text-accent-ink hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-45'
            }
          >
            {spec.commit}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
